import { AsyncPipe, NgIf } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, computed, effect, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { Router, RouterOutlet, RoutesRecognized } from '@angular/router';
import { LocalizeRouterService } from '@gilsdav/ngx-translate-router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { IMessage } from '@stomp/rx-stomp';
import { KEYCLOAK_EVENT_SIGNAL, KeycloakEventType, ReadyArgs, typeEventArgs } from 'keycloak-angular';
import Keycloak, { KeycloakProfile } from 'keycloak-js';
import { get } from 'lodash';
import { MenuItem, MessageService } from 'primeng/api';
import { PrimeNG } from 'primeng/config';
import { filter, map, Subject, take, takeUntil } from 'rxjs';
import { environment } from 'src/environments/environment';

import primeLocaleNl from '../assets/primelocale';

import { ClientService } from './client.service';
import { SUITEVIEWPATH } from './core/core.routing';
import { LayoutService } from './core/services';
import { HelpMenuService } from './help-menu.service';
import { MeetingService } from './meeting/meeting/meeting.service';
import { MeetingActionResponse } from './meeting/meeting/meeting.types';
import { MobileMenuService } from './mobile-menu.service';
import { RealmService } from './realm.service';
import { AnnouncementService } from './repositories/announcements/services/announcement.service';
import { AnnouncementFacade } from './repositories/announcements/store/announcement.facade';
import { AnnouncementEntity } from './repositories/announcements/store/announcement.models';
import {
  CalculationModule,
  CalculationModuleService
} from './repositories/calculation-module/service/calculation-module.service';
import { MeetingPointersStore } from './repositories/meetingpointers/stores/meeting-pointers.store';
import { AppMessageService } from './repositories/message.service';
import {
  PrintListModule,
  PrintListModuleService
} from './repositories/print-list-module/service/print-list-module.service';
import { RepositoriesModule, ThemeFacade } from './repositories/repositories.module';
import { IConfigurationItem, IFavourite, ThemeEntity } from './repositories/theme/store/theme.models';
import { AsyncUserTask, UserTaskStatus, UserTaskType } from './repositories/userdata/store/userdata.models';
import { SearchFacade } from './search/facades/search.facade';
import { FileService } from './shared/services';
import { SharedModule } from './shared/shared.module';
import { UiModule } from './ui/ui.module';
import { UserService } from './user.service';
import { UserDataFacade } from './userdata/facades/userdata.facade';
import { VotingModule } from './voting/voting.module';
import { VotingService } from './voting/voting.service';
import { WalkthroughService } from './walkthrough.service';

import { SuiteBackendRxStompService } from '~/core/websockets/suite-backend-rx-stomp.service';
import { SuiteRxStompService } from '~/core/websockets/suite-rx-stomp.service';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, UiModule, SharedModule, VotingModule, RepositoriesModule, AsyncPipe, NgIf, TranslateModule],
  providers: [
    ThemeFacade,
    UserDataFacade,
    TranslateService,
    AnnouncementFacade,
    HelpMenuService,
    MeetingService,
    ClientService,
    AnnouncementService,
    PrintListModuleService,
    CalculationModuleService,
    WalkthroughService,
    MobileMenuService,
    LocalizeRouterService,
    RealmService,
    ClientService,
    SearchFacade,
    VotingService,
    MessageService,
    ClientService
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly keycloak = inject(Keycloak);
  private readonly keycloakSignal = inject(KEYCLOAK_EVENT_SIGNAL);
  private readonly layoutService = inject(LayoutService);
  private readonly fileService = inject(FileService);
  private readonly meetingPointersStore = inject(MeetingPointersStore);

  useMinimalLayout = this.layoutService.useMinimalLayout;

  public title = 'Green Valley Suite';
  public user: KeycloakProfile;
  public userGivenName: string;
  public starredMenuItems: MenuItem[];
  public userMenuItems = signal<MenuItem[]>([]);
  public configurationMenuItems: MenuItem[];
  public themeLoaded$ = this.themeFacade.loaded$;
  public theme$ = this.themeFacade.theme$;
  public footerInformation$ = this.themeFacade.footerInformation$;
  public exportsLength$ = this.userDataFacade.newExportsLength$;
  public walkthrough$ = this.walkthroughService.walkthroughForCurrentRoute$;
  public mobileMenuItems = this.mobileMenuService.mobileMenuItems;
  public mobileMenuOpen = this.mobileMenuService.mobileMenuOpen;
  public helpMenuItems$ = this.helpMenuService.helpMenuItems$;
  public allRolesActive$ = this.userDataFacade.allRolesActive$;

  public showAnnouncementDialog = false;
  public announcements: AnnouncementEntity[] = [];
  public showNewAnnouncements = false;
  public newAnnouncements: AnnouncementEntity[] = [];

  public printListModule: PrintListModule;
  public calculationModule: CalculationModule;
  public meetingPointers = computed(() => this.meetingPointersStore.data());
  public showMeetingPointersDialog = false;

  public isForbidden = signal<boolean>(false);
  public userHasRoles = signal<boolean>(true);

  private readonly destroyed$: Subject<boolean> = new Subject<boolean>();

  authenticated = false;
  keycloakStatus: string | undefined;

  constructor(
    private themeFacade: ThemeFacade,
    private userDataFacade: UserDataFacade,
    private router: Router,
    private translateService: TranslateService,
    private announcementFacade: AnnouncementFacade,
    private announcementService: AnnouncementService,
    private printListModuleService: PrintListModuleService,
    private calculationModuleService: CalculationModuleService,
    private http: HttpClient,
    private votingService: VotingService,
    private messageService: MessageService,
    private searchFacade: SearchFacade,
    private userService: UserService,
    private clientService: ClientService,
    private primeNgConfig: PrimeNG,
    private walkthroughService: WalkthroughService,
    private mobileMenuService: MobileMenuService,
    private helpMenuService: HelpMenuService,
    private localize: LocalizeRouterService,
    private meetingService: MeetingService,
    private suiteBackendRxStompService: SuiteBackendRxStompService,
    private suiteRxStompService: SuiteRxStompService
  ) {
    this.translateService.setDefaultLang('nl');
    this.primeNgConfig.setTranslation(primeLocaleNl.nl);

    effect(() => {
      const keycloakEvent = this.keycloakSignal();
      this.keycloakStatus = keycloakEvent.type;
      console.info(keycloakEvent.type);

      if (keycloakEvent.type === KeycloakEventType.Ready) {
        this.authenticated = typeEventArgs<ReadyArgs>(keycloakEvent.args);
        if (!this.authenticated) {
          const isGvb = this.router.url === '/gvb';
          this.keycloak.login({
            ...(isGvb && {
              idpHint: 'google',
              redirectUri: window.location.origin
            })
          });
        } else {
          this.handleAuthenticatedUser();
        }
      }
      if (keycloakEvent.type === KeycloakEventType.AuthRefreshError) {
        this.logout();
      }
      if (keycloakEvent.type === KeycloakEventType.AuthLogout) {
        this.authenticated = false;
      }
      if (keycloakEvent.type === KeycloakEventType.TokenExpired) {
        console.info('update');
        this.keycloak.updateToken(20).then(() => {
          environment.BEARER_TOKEN = this.keycloak.token;
        });
      }
    });
  }

  public async ngOnInit() {
    console.info('App component: onInit');
    AppMessageService.setInstance(this.messageService);

    this.router.events
      .pipe(
        filter((event) => event instanceof RoutesRecognized),
        takeUntil(this.destroyed$)
      )
      .subscribe((event) => {
        const { state: eventState } = event as RoutesRecognized;
        this.toggleMobileMenu(false);
        this.walkthroughService.currentWalkthrough = eventState.root.firstChild?.data['key'] || null;
      });

    if (this.router.url === '/403') {
      console.info('Forbidden: true');
      this.isForbidden.set(true);
    } else {
      console.info('Forbidden: false');
      this.isForbidden.set(false);
    }
  }

  private handleAuthenticatedUser() {
    console.info('Next: checkUser');
    this.userService
      .checkUser()
      .pipe(take(1))
      .subscribe({
        error: () => {
          this.keycloak.logout({ redirectUri: window.location.origin + '/403' });
        },
        next: (authenticationRO) => {
          if (this.isRootUrl(this.router.url) && !this.isRootUrl(authenticationRO.startPage.url)) {
            this.userHasRoles.set(false);
            this.router.navigateByUrl(this.translateService.instant(authenticationRO.startPage.url));
          }

          this.keycloak.loadUserProfile().then((user) => {
            this.user = user;
            this.userGivenName = authenticationRO.givenName;
            environment.USERNAME = authenticationRO.username;
            environment.USERID = authenticationRO.userId;
            environment.BEARER_TOKEN = this.keycloak.token;

            this.clientService.clientConfig();
            this.votingService.init();

            // Set up walkthrough
            this.router.routerState.root.firstChild.data.pipe(take(1)).subscribe((data) => {
              this.walkthroughService.currentWalkthrough = (data as { key: string })?.key || null;
            });

            // WebSocket subscriptions
            this.suiteBackendRxStompService.watchTopic('/user/event/meeting/pointers').subscribe((msg: IMessage) => {
              this.meetingPointersStore.set(JSON.parse(msg.body));

              this.mobileMenuService.setMobileMenuItemAtIndex(
                {
                  label: this.translateService.instant('current.meetings'),
                  command: () => {
                    this.openMeetingPointersDialog();
                    this.toggleMobileMenu(false);
                  },
                  badge: this.meetingPointers.length.toString(),
                  badgeStyleClass: 'theme-alternating-color theme-text-color'
                },
                0
              );
            });

            this.suiteBackendRxStompService.watchTopic('/user/event/announcements').subscribe((msg: IMessage) => {
              const announcements: AnnouncementEntity[] = JSON.parse(msg.body);
              if (announcements.length > 0) {
                this.newAnnouncements = announcements;
                this.showNewAnnouncements = true;
                // there's a change in announcements ... reload them
                this.announcementFacade.initAnnouncements();
              }
            });

            this.suiteBackendRxStompService.watchTopic('/user/event/printlist').subscribe((msg: IMessage) => {
              const body = JSON.parse(msg.body) as unknown as PrintListModule;
              this.printListModule = body;
              this.mobileMenuService.setMobileMenuItemAtIndex(
                {
                  ...this.mobileMenuService.mobileMenuItems[2],
                  badge: body.counter.toString(),
                  badgeStyleClass: 'theme-alternating-color theme-text-color'
                },
                2
              );
            });

            this.suiteBackendRxStompService.watchTopic('/user/event/async-user-task').subscribe((msg: IMessage) => {
              const userTask = JSON.parse(msg.body) as AsyncUserTask;
              let startMessage = '';
              let finishedMessage = '';
              let failedMessage = '';
              let titleMessage = '';

              if (userTask.type == UserTaskType.SEARCH_EXPORT) {
                startMessage = 'search.export.generate.start';
                finishedMessage = 'search.export.generate.finished';
                failedMessage = 'search.export.generate.failed';
                titleMessage = 'search.export.generate.title';
              } else if (userTask.type == UserTaskType.PRESENCE_EXPORT) {
                startMessage = 'presence.report.generate.start';
                finishedMessage = 'presence.report.generate.finished';
                failedMessage = 'presence.report.generate.failed';
                titleMessage = 'presence.report.generate.title';
              } else if (userTask.type == UserTaskType.GENERATE_PUBLICATION) {
                startMessage = 'usertask.publication.start';
                finishedMessage = 'usertask.publication.finished';
                failedMessage = 'usertask.publication.failed';
                titleMessage = 'usertask.publication.title';
              } else if (userTask.type == UserTaskType.GENERATE_PUBLICATION_XML) {
                startMessage = 'usertask.publication_xml.start';
                finishedMessage = 'usertask.publication_xml.finished';
                failedMessage = 'usertask.publication_xml.failed';
                titleMessage = 'usertask.publication_xml.title';
              } else if (userTask.type == UserTaskType.MEETING_EXPORT) {
                startMessage = 'meeting.export.generate.start';
                finishedMessage = 'meeting.export.generate.finished';
                failedMessage = 'meeting.export.generate.failed';
                titleMessage = 'meeting.export.generate.title';
              }

              if (userTask.status == UserTaskStatus.QUEUED) {
                this.messageService.add({
                  severity: 'info',
                  summary: this.translateService.instant(titleMessage),
                  detail: `${this.translateService.instant(startMessage, {
                    '0': userTask.name,
                    '1': `<a class='text-gray-800' href=${this.translateService.instant(
                      'ROUTES.userdata'
                    )}/${this.translateService.instant('ROUTES.generatedfiles')}>${this.translateService.instant(
                      'search.export.link.downloadpage'
                    )}</a>`
                  })}`
                });
              } else if (userTask.status == UserTaskStatus.COMPLETED) {
                this.messageService.add({
                  severity: 'success',
                  summary: this.translateService.instant(titleMessage),
                  detail: `${this.translateService.instant(finishedMessage, {
                    '0': userTask.name,
                    '1': `<a class='underline cursor-pointer text-gray-800' id='downloadLink-${userTask.id}'
                  }>${this.translateService.instant('search.export.link.downloadnow')}</a>`
                  })}`
                });
                setTimeout(() => {
                  const el = document.getElementById(`downloadLink-${userTask.id}`);
                  el.addEventListener('click', () => this.handleDownloadTask(userTask));
                  setTimeout(() => {
                    el.removeEventListener('click', () => this.handleDownloadTask(userTask));
                  }, 3000);
                }, 1);
              } else if (userTask.status == UserTaskStatus.FAILED) {
                this.messageService.add({
                  severity: 'error',
                  summary: this.translateService.instant(titleMessage),
                  detail: this.translateService.instant(failedMessage, {
                    '0': userTask.name
                  })
                });
              }
              this.userDataFacade.updateAsyncUserTask(userTask);
            });

            this.suiteBackendRxStompService.watchTopic('/user/event/agenda-action').subscribe((msg: IMessage) => {
              const res = JSON.parse(msg.body) as unknown as MeetingActionResponse;
              this.meetingService.receiveActionResponse(res);
            });

            this.suiteRxStompService.watchTopic('/user/event/allrolesactive').subscribe((msg: IMessage) => {
              this.userDataFacade.setAllRolesActive(JSON.parse(msg.body));
            });

            this.suiteRxStompService.watchTopic('/user/event/bulk-action').subscribe((msg: IMessage) => {
              this.messageService.add({
                severity: 'success',
                detail: msg.body,
                life: 5000
              });
            });

            // Set up user menu
            this.userMenuItems.set([
              {
                label: this.translateService.instant('preferences.menu.roles'),
                icon: 'fa-regular fa-gear',
                routerLink: `${SUITEVIEWPATH}/preferences/roles`,
                target: '_self'
              },
              {
                label: this.translateService.instant('preferences.menu.logout'),
                icon: 'fa-regular fa-power-off',
                command: () => this.logout()
              }
            ]);

            this.mobileMenuService.setMobileMenuItemAtIndex(
              {
                label: `${user.firstName} ${get(user, ['attributes', 'middleName'], []) as string[][0]} ${user.lastName}`,
                icon: 'fa-solid fa-user',
                items: this.userMenuItems()
              },
              6
            );

            // Initialize services
            const organisation = 123;
            this.themeFacade.init(organisation);
            this.themeFacade.loadFavourites();
            this.themeFacade.loadHelpInformation();
            this.themeFacade.loadFooterInformation();
            this.themeFacade.loadConfigurationItems();
            this.userDataFacade.fetchAsyncUserTasks();
            this.userDataFacade.fetchUserPreferences();
            this.searchFacade.fetchQueries();
            this.helpMenuService.init();
            this.announcementFacade.initAnnouncements();

            // Theme handling
            this.theme$
              .pipe(
                filter((theme) => !!theme),
                take(1),
                map((theme: ThemeEntity) => {
                  const style = document.createElement('style');
                  style.innerHTML = `
                      .empty-state-icon {
                        color: ${theme.backgroundColor} !important;
                      }
                      .theme-background-color, .p-drawer-header {
                        background-color: ${theme.backgroundColor} !important;
                      }
                      .theme-text-color {
                        color: ${theme.textColor} !important;
                      }
                      .theme-alternating-color {
                        background-color: ${theme.alternatingColor} !important;
                      }
                      .theme-border-color {
                        border-color: ${theme.alternatingColor} !important;
                      }
                      .p-progressspinner .p-progressspinner-circle {
                        stroke: ${theme.alternatingColor} !important;
                      }
                    `;
                  return document.head.appendChild(style);
                })
              )
              .subscribe();

            // Favourites handling
            this.themeFacade.favourites$
              .pipe(
                filter((theme) => !!theme),
                take(1),
                map((favourites: IFavourite[]) => {
                  this.starredMenuItems = favourites.map((item) => {
                    return {
                      label: item.label,
                      url: item.allFavouritesUrl,
                      items: item.favouriteTypes.map((type) => {
                        return {
                          label: type.name,
                          url: type.url
                        };
                      })
                    };
                  });
                })
              )
              .subscribe();

            // Service subscriptions
            this.meetingPointersStore.fetch();

            this.announcementService
              .getNewAnnouncements()
              .pipe(takeUntil(this.destroyed$))
              .subscribe((newAnnouncements) => {
                if (newAnnouncements.length > 0) {
                  this.newAnnouncements = newAnnouncements;
                  this.showNewAnnouncements = true;
                }
              });

            this.printListModuleService
              .getPrintList()
              .pipe(takeUntil(this.destroyed$))
              .subscribe((printList) => {
                this.printListModule = printList;
                this.mobileMenuService.setMobileMenuItemAtIndex(
                  {
                    label: this.translateService.instant('information.printlist.title'),
                    badge: printList?.counter?.toString(),
                    badgeStyleClass: 'theme-alternating-color theme-text-color',
                    routerLink: [SUITEVIEWPATH, 'print']
                  },
                  2
                );
              });

            this.calculationModuleService
              .getCalculationModule()
              .pipe(takeUntil(this.destroyed$))
              .subscribe((calculationModule) => {
                if (calculationModule) {
                  this.calculationModule = calculationModule;
                  this.mobileMenuService.setMobileMenuItemAtIndex(
                    {
                      label: this.translateService.instant('information.calculation.title'),
                      routerLink: SUITEVIEWPATH + calculationModule.url
                    },
                    3
                  );
                }
              });

            // Configuration items
            this.themeFacade.configurationItems$
              .pipe(
                filter((configurationItems) => !!configurationItems?.length),
                take(1),
                map((configurationItems: IConfigurationItem[]) => {
                  this.configurationMenuItems = configurationItems.map((item) => {
                    if (item.routeType === 'FRONTENDROUTE') {
                      return {
                        label: item.name,
                        routerLink: this.localize.translateRoute(item.url),
                        target: item.target || '_self'
                      };
                    }
                    return {
                      label: item.name,
                      routerLink: `${SUITEVIEWPATH}/${item.url}`,
                      target: item.target || '_self'
                    };
                  });
                  this.mobileMenuService.setMobileMenuItemAtIndex(
                    {
                      label: this.translateService.instant('information.configuration.title'),
                      items: this.configurationMenuItems,
                      icon: 'fa-regular fa-gear'
                    },
                    7
                  );
                })
              )
              .subscribe();

            // Announcements subscription
            this.announcementFacade.allAnnouncements$.pipe(takeUntil(this.destroyed$)).subscribe((announcements) => {
              if (announcements.length) {
                this.announcements = announcements;
                this.mobileMenuService.setMobileMenuItemAtIndex(
                  {
                    label: this.translateService.instant('information.announcements.title'),
                    command: () => {
                      this.openAnnouncementDialog();
                      this.toggleMobileMenu(false);
                    },
                    badge: announcements.length.toString(),
                    badgeStyleClass: 'theme-alternating-color theme-text-color'
                  },
                  1
                );
              }
            });

            // Generated files menu
            this.mobileMenuService.setMobileMenuItemAtIndex(
              {
                label: this.translateService.instant('userdata.generatedfiles.title'),
                routerLink: this.localize.translateRoute('ROUTES.userdata/ROUTES.generatedfiles'),
                badgeStyleClass: 'theme-alternating-color theme-text-color'
              },
              4
            );

            this.userDataFacade.newExportsLength$.pipe(takeUntil(this.destroyed$)).subscribe((exportsLength) => {
              this.mobileMenuService.setMobileMenuItemAtIndex(
                {
                  ...this.mobileMenuService.mobileMenuItems[4],
                  badge: exportsLength.toString(),
                  badgeStyleClass: 'theme-alternating-color theme-text-color'
                },
                4
              );
            });

            // All roles active subscription
            this.userDataFacade.allRolesActive$.pipe(takeUntil(this.destroyed$)).subscribe((allRolesActive) => {
              if (!allRolesActive) {
                this.mobileMenuService.setMobileMenuItemAtIndex(
                  {
                    ...this.mobileMenuService.mobileMenuItems[6],
                    icon: 'fa-solid fa-user-xmark',
                    items: [...this.mobileMenuService.mobileMenuItems[6].items]
                  },
                  6
                );
              } else {
                this.mobileMenuService.setMobileMenuItemAtIndex(
                  {
                    ...this.mobileMenuService.mobileMenuItems[6],
                    icon: 'fa-solid fa-user',
                    items: [...this.mobileMenuService.mobileMenuItems[6].items]
                  },
                  6
                );
              }
            });
          });
        }
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  handleSearch(text: string) {
    this.searchFacade.reset();
    localStorage.removeItem('search.current.state');
    this.router.navigateByUrl('/').then(() =>
      this.router.navigate([this.translateService.instant('ROUTES.search')], {
        queryParams: { query: text },
        replaceUrl: true
      })
    );
  }

  toggleMobileMenu(open: boolean) {
    this.mobileMenuService.mobileMenuOpen = open;
  }

  openAnnouncementDialog() {
    this.showAnnouncementDialog = true;
  }

  openMeetingPointersDialog() {
    this.showMeetingPointersDialog = true;
  }

  logout() {
    this.logoutOldBackend();
    environment.USERNAME = null;
    environment.BEARER_TOKEN = null;
    this.keycloak.logout({ redirectUri: window.location.origin });
  }

  logoutOldBackend() {
    this.http
      .get(`${environment.OLD_BACKEND_URL}/sso/logout`)
      .pipe(take(1))
      .subscribe({
        error: (error) => {
          console.info('Error suite logout!');
          console.info(error);
        }
      });
  }

  markAnnouncementsViewed(announcementIds: string[]) {
    this.announcementFacade.markAnnouncementsViewed(announcementIds);
    this.showAnnouncementDialog = false;
  }

  markNewAnnouncementsViewed(announcementIds: string[]) {
    this.announcementFacade.markAnnouncementsViewed(announcementIds);
    this.showNewAnnouncements = false;
  }

  handleDownloadTask = async (task: AsyncUserTask) => {
    try {
      if (task.inlineView) {
        await this.fileService.openFileInViewer(
          `${environment.BACKEND_URL}${task.documentDownloadPath}`,
          task.documentName,
          { canDownload: task.canDownload }
        );
      } else {
        await this.fileService.downloadFile(
          `${environment.BACKEND_URL}${task.documentDownloadPath}`,
          task.documentName
        );
      }

      this.userDataFacade.updateAsyncUserTask({ ...task, downloaded: true });
    } catch (err) {
      console.error(err);
      this.messageService.add({
        severity: 'error',
        detail: this.translateService.instant('error.document.download'),
        life: 5000
      });
    }
  };

  handleCloseWalkthrough() {
    this.walkthroughService.setViewed();
  }

  isRootUrl(url: string): boolean {
    return url === '/' || url.startsWith('/#');
  }
}
